import { FlaskConical, KeyRound, LoaderCircle, ShieldCheck } from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "../api";
import { EmptyState, LoadingState, MetricStrip, PageHeader, SectionHeading, StatusBadge, formatDate } from "../components";
import { useAppData } from "../context";
import type { Account, CapabilityStatus } from "../types";

export function Accounts() {
  const { data, refresh } = useAppData();
  const [selectedId, setSelectedId] = useState("");
  const [busy, setBusy] = useState("");
  const [message, setMessage] = useState("");
  useEffect(() => {
    if (!selectedId && data?.accounts[0]) setSelectedId(data.accounts[0].id);
  }, [data, selectedId]);
  if (!data) return <LoadingState />;
  const selected = data.accounts.find((account) => account.id === selectedId) ?? data.accounts[0];

  async function runProductionTest(account: Account) {
    setBusy(account.id);
    setMessage("");
    try {
      const result = await api.runProductionTest(account.id);
      setMessage(`${account.accountCode} 只读生产测试${result.status === "PASSED" ? "通过" : "部分通过"}，读取 ${result.activeItemCount.toLocaleString()} 个在售商品，抽检 ${result.sampledItemCount} 个；未发送商品写请求。`);
      await refresh();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "只读生产测试失败。" );
    } finally {
      setBusy("");
    }
  }

  return <>
    <PageHeader eyebrow="ACCOUNTS / CAPABILITIES" title="账号与站点能力" />
    {message ? <div className="inline-message" role="status">{message}</div> : null}
    <section className="workspace-band">
      <SectionHeading title="授权账号" meta={`${data.accounts.length} 个账号`} />
      {data.accounts.length ? <div className="table-scroll"><table className="data-table clickable-rows">
        <thead><tr><th>账号</th><th>Seller ID</th><th>模式</th><th>站点</th><th>授权状态</th><th>Token 到期</th><th aria-label="操作" /></tr></thead>
        <tbody>{data.accounts.map((account) => <tr key={account.id} role="button" tabIndex={0} className={selected?.id === account.id ? "selected" : ""} onClick={() => setSelectedId(account.id)} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") setSelectedId(account.id); }}>
          <td><strong className="mono">{account.accountCode}</strong></td><td className="mono">{account.sellerId}</td><td>{account.mode}</td><td>{account.sites.join(", ") || "待检测"}</td>
          <td><StatusBadge status={account.status === "ACTIVE" ? "SUCCESS" : account.status === "EXPIRING" ? "NEEDS_REVIEW" : "FAILED"} /></td>
          <td>{formatDate(account.tokenExpiresAt)}</td>
          <td><div className="table-actions"><button className="icon-button" aria-label={`重新授权 ${account.accountCode}`} title="重新授权 Mercado Libre" onClick={(event) => { event.stopPropagation(); window.location.assign(`/api/auth/mercadolibre/start?account_code=${encodeURIComponent(account.accountCode)}`); }}><KeyRound size={16} /></button><button className="icon-button" disabled={busy === account.id} aria-label={`只读生产测试 ${account.accountCode}`} title="只读生产测试，不修改商品" onClick={(event) => { event.stopPropagation(); void runProductionTest(account); }}>{busy === account.id ? <LoaderCircle size={16} className="spin" /> : <FlaskConical size={16} />}</button></div></td>
        </tr>)}</tbody>
      </table></div> : <EmptyState title="尚未接入真实授权账号" />}
    </section>
    {selected?.productionTest ? <section className="workspace-band">
      <SectionHeading title="最近一次生产只读测试" meta={`${formatDate(selected.productionTest.checkedAt)} · 未调用写接口`} />
      <MetricStrip items={[
        { label: "测试结果", value: selected.productionTest.status === "PASSED" ? "通过" : "部分通过", tone: selected.productionTest.status === "PASSED" ? "positive" : "warning" },
        { label: "在售商品", value: selected.productionTest.activeItemCount.toLocaleString() },
        { label: "抽检商品", value: selected.productionTest.sampledItemCount },
        { label: "账号归属", value: selected.productionTest.checks.ownership ? "一致" : "未确认", tone: selected.productionTest.checks.ownership ? "positive" : "warning" },
        { label: "费用读取", value: selected.productionTest.checks.listingFees ? "通过" : "未通过", tone: selected.productionTest.checks.listingFees ? "positive" : "warning" },
      ]} />
    </section> : null}
    {selected ? <section className="workspace-band capability-section">
      <SectionHeading title={`${selected.accountCode} 能力矩阵`} meta={`最近检查 ${formatDate(selected.lastCheckedAt)}`} />
      {selected.capabilities.length ? <div className="table-scroll"><table className="data-table capability-table">
        <thead><tr><th>站点</th><th>读取商品</th><th>目标收益</th><th>标题</th><th>描述</th><th>生产状态</th></tr></thead>
        <tbody>{selected.capabilities.map((capability) => <tr key={capability.siteId}>
          <td><strong>{capability.siteId}</strong></td><td><Capability value={capability.itemRead} /></td><td><Capability value={capability.netProceeds} /></td><td><Capability value={capability.title} /></td><td><Capability value={capability.description} /></td>
          <td><span className="locked-cell"><ShieldCheck size={16} />写入锁定</span></td>
        </tr>)}</tbody>
      </table></div> : <EmptyState title="运行只读生产测试后生成能力矩阵" />}
    </section> : null}
  </>;
}

function Capability({ value }: { value: CapabilityStatus }) {
  const label = value === "READ_ONLY_VERIFIED" ? "只读已验证" : value === "VERIFIED" ? "已验证" : value === "MOCK_ONLY" ? "仅测试" : "不可用";
  return <span className={`capability ${value.toLowerCase()}`}>{label}</span>;
}
